import { ChangeEvent, FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntityForm } from '../../../entities/hooks';
import { DateFieldController, TextFieldController } from '../../../../components/inputs';
import { Box, Stack, Button, Card, CardHeader, CardContent, Grid, TextField } from '@mui/material';
import { LOCALE_CR, LOCALE_UZ, LOCALE_RU } from '../../../../common/constants';
import {
  OPEN_INFO_TITLE,
  OPEN_INFO_FILES,
  OPEN_INFO_TYPE,
  OPEN_INFO_FILE_ID,
  OPEN_INFO_CREATED_AT,
} from '../../constants';
import { IconClearAll, IconSave } from '../../../../common/icons';
import { IForm } from '../../../../common/models';
import { OpenInfoSchema } from '../../schema';
import { IOpenInfo } from '../../models';
import { SelectOpenInfoController } from '../input';
import { useSnackbar } from '../../../../common/hooks';
import { useFiles } from '../../../files';
import { get } from '../../../../common/utils';
import WarningSnackbarComponent from '../../../../components/warning-snockbar/WarningSnockbarComponent';
import { useWatch } from 'react-hook-form';

const validFileTypes: any = {
  OPEN: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'image/jpeg',
    'image/png',
  ],
  DEPARTMENT: ['.jpg', 'jpeg', 'image/jpg', 'image/jpeg', '.png', 'image/png'],
};
export const OpenInfoForms: FC<IForm<IOpenInfo>> = ({ defaultValues, onValid, isLoading }) => {
  const { t } = useTranslation();
  const [fileTypeError, setFileTypeError] = useState<boolean>(false);
  const { showMessage } = useSnackbar();
  const { handleInputUpload } = useFiles();
  const { control, onReset, onSubmit, getValues, setValue } = useEntityForm<IOpenInfo>({
    schema: OpenInfoSchema,
    defaultValues,
    onValid,
  });

  const infoType = useWatch({ control, name: OPEN_INFO_TYPE });

  const isDepartament = useMemo(() => infoType === 'DEPARTMENT', [infoType]);

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const fileObj = e.target.files && e.target.files[0];

    if (fileObj && validFileTypes[infoType].includes(fileObj?.type)) {
      handleInputUpload(e)
        .then((res: any) => {
          setValue(OPEN_INFO_FILE_ID, res?.id);
          console.log(getValues(OPEN_INFO_FILE_ID));
          setValue(OPEN_INFO_FILES, res);
        })
        .catch((e) => {
          const message = get(e, 'response.data.message', t('error'));
          showMessage(message, { variant: 'filled', severity: 'error' });
        });
    } else {
      setFileTypeError(true);
      e.target.files = null;
      e.target.value = '';
    }
  };

  return (
    <Box onSubmit={onSubmit} component="form">
      <WarningSnackbarComponent
        state={fileTypeError}
        setState={setFileTypeError}
        label={`Ҳужжат тури ${validFileTypes[infoType]?.join(
          ' ',
        )} ва ҳажми 100Мб дан ошмаслиги лозим!`}
      />
      <Stack direction="row" justifyContent="flex-end" spacing={1.5} mb={2.5}>
        <Button
          type="reset"
          variant="contained"
          color="secondary"
          startIcon={<IconClearAll />}
          disabled={isLoading}
          onClick={onReset}
        >
          {t('action.clear')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<IconSave />}
          disabled={isLoading}
        >
          {t('action.save')}
        </Button>
      </Stack>
      <Card>
        <CardHeader title={t('open.info.form')} />
        <CardContent>
          {isDepartament ? (
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6} lg={3}>
                <DateFieldController
                  control={control}
                  name={OPEN_INFO_CREATED_AT}
                  label={t('open.info.field.created.at')}
                  disabled={isLoading}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  onChange={handleFileUpload}
                  label={t('open.info.field.files')}
                  type="file"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <SelectOpenInfoController
                  control={control}
                  name={OPEN_INFO_TYPE}
                  label={t('open.info.field.type')}
                  disabled={isLoading}
                  fullWidth
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6} lg={3}>
                <TextFieldController
                  control={control}
                  name={[OPEN_INFO_TITLE, LOCALE_CR].join('.')}
                  disabled={isLoading}
                  label={t('open.info.field.name.cr')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextFieldController
                  control={control}
                  name={[OPEN_INFO_TITLE, LOCALE_UZ].join('.')}
                  disabled={isLoading}
                  label={t('open.info.field.name.uz')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextFieldController
                  control={control}
                  name={[OPEN_INFO_TITLE, LOCALE_RU].join('.')}
                  disabled={isLoading}
                  label={t('open.info.field.name.ru')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <SelectOpenInfoController
                  control={control}
                  name={OPEN_INFO_TYPE}
                  label={t('open.info.field.type')}
                  disabled={isLoading}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <DateFieldController
                  control={control}
                  name={OPEN_INFO_CREATED_AT}
                  label={t('open.info.field.created.at')}
                  disabled={isLoading}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  onChange={handleFileUpload}
                  label={t('open.info.field.files')}
                  type="file"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
